import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Popover, Switch, Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Icons } from '../shared/utils/icons.jsx'
import usePushNotifications from './usePushNotifications.js'
import { disablePushNotifications } from './pushNotifications.js'

const ELIGIBLE_ROLES = ['FACULTY', 'HOD', 'PRINCIPAL']

const StaffPushNotifications = ( { apiClient, currentUser } ) => {
    const navigate = useNavigate()
    const { palette } = useTheme()

    const role = String( currentUser?.user_role || '' ).toUpperCase()
    const coordinatorPermission = currentUser?.features_permissions?.placement_coordinator
    const branches = Array.isArray( coordinatorPermission?.branches ) ? coordinatorPermission.branches : []

    const canReceive = ELIGIBLE_ROLES.includes( role ) && branches.length > 0

    const push = usePushNotifications( {
        apiClient,
        audience: 'coordinator',
        enabled: canReceive
    } )

    const [anchorEl, setAnchorEl] = useState( null )
    const [promptOpen, setPromptOpen] = useState( false )
    const [inAppNotifications, setInAppNotifications] = useState( [] )

    const activeNotification = inAppNotifications[0] || null
    const userId = currentUser?.user_id || currentUser?.empcode || currentUser?.email || currentUser?.user_email || ''
    const promptKey = userId ? `staff-notification-prompt-${userId}` : ''

    useEffect( () => {
        if ( !role || canReceive || !apiClient ) return

        disablePushNotifications( apiClient, 'coordinator' ).catch( () => { } )
    }, [role, canReceive, apiClient] )

    useEffect( () => {
        if ( !canReceive || !promptKey || !push.supported || push.permission === 'denied' || push.subscribed ) return
        if ( localStorage.getItem( promptKey ) ) return

        localStorage.setItem( promptKey, 'shown' )
        setPromptOpen( true )
    }, [canReceive, promptKey, push.supported, push.permission, push.subscribed] )

    useEffect( () => {
        if ( !canReceive || typeof navigator === 'undefined' || !( 'serviceWorker' in navigator ) ) return

        const handleMessage = event => {
            const message = event.data
            if ( message?.type !== 'placement-push-received' || !message.notification ) return

            setInAppNotifications( previous => {
                const incoming = message.notification
                const rest = incoming.tag ? previous.filter( notification => notification.tag !== incoming.tag ) : previous
                return [...rest, { ...incoming, id: `${Date.now()}-${Math.random()}` }]
            } )
        }

        navigator.serviceWorker.addEventListener( 'message', handleMessage )
        navigator.serviceWorker.startMessages?.()

        return () => navigator.serviceWorker.removeEventListener( 'message', handleMessage )
    }, [canReceive] )

    const goToUrl = url => {
        if ( !url ) return

        const target = new URL( url, window.location.origin )
        if ( target.origin === window.location.origin ) {
            navigate( target.pathname + target.search + target.hash )
        } else {
            window.location.href = target.href
        }
    }

    const handleToggle = async event => {
        try {
            if ( event.target.checked ) {
                await push.enable()
            } else {
                await push.disable()
            }
        } catch ( err ) {
            console.error( err )
        }
    }

    const handleEnableFromPrompt = async () => {
        try {
            await push.enable()
        } finally {
            setPromptOpen( false )
        }
    }

    const handleDismissInAppNotification = () => {
        setInAppNotifications( previous => previous.slice( 1 ) )
    }

    const handleViewInAppNotification = () => {
        const url = activeNotification?.url
        handleDismissInAppNotification()
        goToUrl( url )
    }

    if ( !canReceive ) return null

    return (
        <>
            <Tooltip title='Placement notifications'>
                <IconButton
                    size='small'
                    onClick={event => setAnchorEl( event.currentTarget )}
                    sx={{ color: 'inherit' }}
                >
                    <Badge color='error' variant='dot' invisible={push.subscribed || !push.supported}>
                        {Icons.default.NotificationsOutlined}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean( anchorEl )}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl( null )}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 300, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>Placement notifications</Typography>
                    <Typography variant='caption' color='text.secondary'>
                        Get notified when a student submits or updates their placement profile for your assigned branches.
                    </Typography>

                    <FormControlLabel
                        labelPlacement='start'
                        label={<Typography variant='body2'>Enabled on this browser</Typography>}
                        control={
                            <Switch
                                size='small'
                                checked={push.subscribed}
                                onChange={handleToggle}
                                disabled={push.busy || !push.supported}
                            />
                        }
                        sx={{ p: 1, borderRadius: 4, bgcolor: palette.contentBg, m: 0, justifyContent: 'space-between', border: `1px solid ${palette.primary.light}` }}
                    />

                    {!push.supported && (
                        <Typography variant='caption' color='error.main'>
                            This browser does not support push notifications.
                        </Typography>
                    )}

                    {push.supported && push.permission === 'denied' && (
                        <Typography variant='caption' color='error.main'>
                            Notifications are blocked in your browser settings.
                        </Typography>
                    )}

                    <Typography variant='caption' color='text.secondary'>
                        If your placement access was just changed, sign in again before enabling notifications.
                    </Typography>
                </Box>
            </Popover>

            <Dialog open={promptOpen} onClose={() => setPromptOpen( false )} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '15px', fontWeight: 'bold', pb: 1 }}>Enable placement notifications</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Typography variant='body2' sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5 }}>
                        Get notified when a student submits or updates their placement profile for your assigned branches.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button size='small' color='inherit' onClick={() => setPromptOpen( false )} sx={{ fontSize: '12px' }}>
                        Not now
                    </Button>
                    <Button size='small' variant='contained' disableElevation onClick={handleEnableFromPrompt} disabled={push.busy} sx={{ fontSize: '12px' }}>
                        Enable
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean( activeNotification )}
                onClose={handleDismissInAppNotification}
                maxWidth='xs'
                fullWidth
                aria-labelledby='staff-in-app-notification-title'
            >
                {activeNotification && (
                    <>
                        <DialogTitle
                            id='staff-in-app-notification-title'
                            sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', fontWeight: 'bold', pb: 1 }}
                        >
                            {Icons.default.NotificationsOutlined}
                            {activeNotification.title}
                        </DialogTitle>
                        <DialogContent sx={{ pb: 1 }}>
                            {activeNotification.body && (
                                <Typography variant='body2' sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                                    {activeNotification.body}
                                </Typography>
                            )}
                            {inAppNotifications.length > 1 && (
                                <Typography variant='caption' sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
                                    {inAppNotifications.length - 1} more {inAppNotifications.length - 1 === 1 ? 'update' : 'updates'} waiting
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button size='small' variant='text' color='inherit' onClick={handleDismissInAppNotification} sx={{ fontSize: '12px' }}>
                                {inAppNotifications.length > 1 ? 'Next' : 'Dismiss'}
                            </Button>
                            {activeNotification.url && (
                                <Button size='small' variant='contained' disableElevation onClick={handleViewInAppNotification} sx={{ fontSize: '12px' }}>
                                    View
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    )
}

export default StaffPushNotifications
